import {
  Button,
  PaginationComponent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@folio/components';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { useQuery } from '@tanstack/react-query';
import {
  brokerControllerListBrokersQueryOptions,
  type ListChildBrokerageFirmsOutputData,
  useBrokerageFirmControllerUpdateBrokerageFirm,
  useBrokerControllerDeleteBroker,
} from 'korint-sdk';
import { useKorintModuleConfig } from 'korint-sdk/react';
import {
  ChevronDown,
  ChevronRight,
  UserPlus,
  SquarePenIcon,
  XIcon,
  CheckIcon,
  Trash2,
} from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import { TeamUserInviteDialog } from './TeamUserInviteDialog';
import { formatDate } from '@folio/lib';
import { useCurrentPage } from '@folio/hooks/useCurrentPage';
import { InformationField } from '@folio/components/module-elements/InformationCard/InformationField';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Title } from '@folio/components/ui/text';

interface TeamManagementRowProps {
  team: ListChildBrokerageFirmsOutputData;
  onRefetch: () => void;
}

export function TeamManagementRow({ team, onRefetch }: TeamManagementRowProps) {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isEditingContactName, setIsEditingContactName] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const toast = useToast();

  const getCustomFieldValue = useCallback(
    (key: string): string => {
      const value = team.customFields?.find(cf => cf.key === key)?.value;
      return value ? String(value) : '';
    },
    [team.customFields],
  );

  const teamManagementConfig = useKorintModuleConfig('teamManagement');

  const PAGE_SIZE = 5;
  const membersPageSlug = `members-${team.id}`;
  const membersCurrentPage = useCurrentPage(membersPageSlug);

  const {
    data: members,
    status: membersStatus,
    isFetching: isMembersFetching,
    refetch: refetchMembers,
  } = useQuery({
    ...brokerControllerListBrokersQueryOptions(team.id, {
      count: PAGE_SIZE,
      page: membersCurrentPage,
    }),
  });

  const { mutateAsync: deleteBroker } = useBrokerControllerDeleteBroker({
    mutation: {
      onSuccess: () => {
        toast.toast({
          title: t('teamManagement.toastSuccessTitle'),
          description: t('teamManagement.members.deleteSuccess'),
        });
        refetchMembers();
      },
    },
  });

  const { mutateAsync: updateBrokerageFirm, isPending: isUpdatingBrokerageFirm } =
    useBrokerageFirmControllerUpdateBrokerageFirm({
      mutation: {
        onSuccess: () => {
          toast.toast({
            title: t('teamManagement.toastSuccessTitle'),
            description: t('teamManagement.row.updateSuccess'),
          });
          onRefetch();
        },
        onError: error => {
          toast.toast({
            title: t('teamManagement.toastErrorTitle'),
            description: error.userError || t('teamManagement.row.updateError'),
          });
        },
      },
    });

  const productNames = useMemo(() => {
    if (!team.productIdsWithStatus || team.productIdsWithStatus.length === 0) {
      return t('teamManagement.row.noProducts');
    }
    return team.productIdsWithStatus.map(productId => t(`products.${productId}.label`)).join(', ');
  }, [team.productIdsWithStatus, t]);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const brokerContactFirstName = useMemo(
    () => getCustomFieldValue('brokerContactFirstName'),
    [getCustomFieldValue],
  );
  const brokerContactLastName = useMemo(
    () => getCustomFieldValue('brokerContactLastName'),
    [getCustomFieldValue],
  );
  const brokerEquipe = useMemo(() => getCustomFieldValue('brokerEquipe'), [getCustomFieldValue]);

  const handleStartEditContactName = useCallback(() => {
    setEditedFirstName(brokerContactFirstName);
    setEditedLastName(brokerContactLastName);
    setIsEditingContactName(true);
  }, [brokerContactFirstName, brokerContactLastName]);

  const handleCancelEditContactName = useCallback(() => {
    setEditedFirstName('');
    setEditedLastName('');
    setIsEditingContactName(false);
  }, []);

  const handleSaveEditContactName = useCallback(async () => {
    await updateBrokerageFirm({
      brokerageFirmId: team.id,
      data: {
        customFields: [
          { key: 'brokerContactFirstName', value: editedFirstName },
          { key: 'brokerContactLastName', value: editedLastName },
        ],
      },
    });
    setIsEditingContactName(false);
  }, [updateBrokerageFirm, team.id, editedFirstName, editedLastName]);

  const displayName = useMemo(
    () => [brokerContactFirstName, brokerContactLastName].filter(Boolean).join(' ').trim() || '-',
    [brokerContactFirstName, brokerContactLastName],
  );

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-gray-50" onClick={handleExpandToggle}>
        <TableCell onClick={e => e.stopPropagation()}>
          <InformationField
            fieldKey="brokerEquipe"
            label=""
            updateEntity={async (_, value) => {
              await updateBrokerageFirm({
                brokerageFirmId: team.id,
                data: {
                  customFields: [{ key: 'brokerEquipe', value: value as string }],
                },
              });
            }}
            editionMode="INLINE"
            type="text"
            value={brokerEquipe}
          />
        </TableCell>
        {teamManagementConfig.columns?.map(column => {
          if (column.name === 'brokerContactName') {
            return (
              <TableCell key={column.name} onClick={e => e.stopPropagation()}>
                {isEditingContactName ? (
                  <Group gap={2}>
                    <Input
                      value={editedFirstName}
                      onChange={e => setEditedFirstName(e.target.value)}
                      placeholder={t('teamManagement.row.firstNamePlaceholder')}
                      className="w-32"
                      onClick={e => e.stopPropagation()}
                    />
                    <Input
                      value={editedLastName}
                      onChange={e => setEditedLastName(e.target.value)}
                      placeholder={t('teamManagement.row.lastNamePlaceholder')}
                      className="w-32"
                      onClick={e => e.stopPropagation()}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        handleSaveEditContactName();
                      }}
                      disabled={isUpdatingBrokerageFirm}
                    >
                      <CheckIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        handleCancelEditContactName();
                      }}
                      disabled={isUpdatingBrokerageFirm}
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </Group>
                ) : (
                  <Group gap={2}>
                    <span>{displayName}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        handleStartEditContactName();
                      }}
                      disabled={isUpdatingBrokerageFirm}
                    >
                      <SquarePenIcon className="w-4 h-4" />
                    </Button>
                  </Group>
                )}
              </TableCell>
            );
          }
          return <TableCell key={column.name}>{column.accessor(team) || '-'}</TableCell>;
        })}
        <TableCell>{formatDate(new Date(team.createdAt))}</TableCell>
        <TableCell>{productNames}</TableCell>
        <TableCell>{members?.totalCount ?? '-'}</TableCell>
        <TableCell>
          <Group gap={2}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={e => {
                    e.stopPropagation();
                    setIsInviteDialogOpen(true);
                  }}
                  className="hover:bg-blue-50 text-muted-foreground hover:text-blue-600"
                >
                  <UserPlus className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('teamManagement.row.addMemberTooltip')}</TooltipContent>
            </Tooltip>
            <Button variant="ghost" size="xs" className="hover:bg-gray-50">
              {isExpanded ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          </Group>
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow>
          <TableCell colSpan={6 + (teamManagementConfig.columns?.length || 0)} className="p-0">
            <div className="p-4 bg-gray-50 border-t">
              <Title order={4} className="mb-3 font-medium">
                {t('teamManagement.members.sectionTitle')}
              </Title>
              {membersStatus === 'pending' ? (
                <Stack gap={2}>
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <LoadingRow key={idx} />
                  ))}
                </Stack>
              ) : membersStatus === 'error' ? (
                <ErrorRow />
              ) : members && members.data.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('teamManagement.members.columns.firstName')}</TableHead>
                        <TableHead>{t('teamManagement.members.columns.lastName')}</TableHead>
                        <TableHead>{t('teamManagement.members.columns.email')}</TableHead>
                        <TableHead>{t('teamManagement.members.columns.role')}</TableHead>
                        <TableHead>{t('teamManagement.members.columns.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members.data.map(member => (
                        <TableRow key={member.userId}>
                          <TableCell>{member.firstName}</TableCell>
                          <TableCell>{member.lastName}</TableCell>
                          <TableCell>{member.email}</TableCell>
                          <TableCell>{t(`roles.${member.brokerRole}`)}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() =>
                                deleteBroker({
                                  brokerageFirmId: team.id,
                                  brokerUserId: member.userId,
                                })
                              }
                              className="hover:bg-red-50 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {members && members.totalCount > PAGE_SIZE && (
                    <div className="mt-4">
                      <PaginationComponent
                        totalData={members.totalCount}
                        PAGE_SIZE={PAGE_SIZE}
                        isLoading={isMembersFetching}
                        slug={membersPageSlug}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-muted-foreground py-4">
                  {t('teamManagement.members.empty')}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}

      <TeamUserInviteDialog
        isOpen={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        teamId={team.id}
        teamName={team.companyInfo?.name || t('teamManagement.row.fallbackTeamName')}
        onSuccess={() => {
          setIsInviteDialogOpen(false);
          onRefetch();
        }}
      />
    </>
  );
}
